import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Container, Typography, Button, Popover, CircularProgress, Alert, Paper } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';


function VdiLinkPopover({ lab }) {
    const vdiUrl = lab.baseImage?.destination?.url;
    if (!vdiUrl) return null;

    return (
        <PopupState variant="popover" popupId={`popup-${lab.idLab}`}>
            {(popupState) => (
                <Box>
                    <Button
                        variant="contained"
                        startIcon={<LinkIcon />}
                        size="small"
                        {...bindTrigger(popupState)}
                        sx={{ backgroundColor: '#a71e3b', '&:hover': { backgroundColor: '#7a1629' } }}
                    >
                        Link VM
                    </Button>
                    <Popover
                        {...bindPopover(popupState)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Box sx={{ p: 2, maxWidth: 450 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                                Accesso al Laboratorio
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                                Per accedere alle VM del laboratorio scegli uno dei metodi:
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                                <strong>Opzione A:</strong> Browser HTML5
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ mb: 1.5, color: '#666' }}>
                                Usa un browser (Chrome, Edge, Firefox) e accedi tramite l'URL sottostante
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                                <strong>Opzione B:</strong> VMware Horizon Client
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ mb: 2, color: '#666' }}>
                                Specifica questo URL come "Connection Server"
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f9f9f9', borderColor: '#a71e3b' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontFamily: 'monospace',
                                        wordBreak: 'break-all',
                                        display: 'block',
                                        mb: 1
                                    }}
                                >
                                    {vdiUrl}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    endIcon={<LinkIcon fontSize="small" />}
                                    href={vdiUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: '#a71e3b', borderColor: '#a71e3b' }}
                                >
                                    Apri
                                </Button>
                            </Paper>
                        </Box>
                    </Popover>
                </Box>
            )}
        </PopupState>
    );
}


function getDocenteEmail(lab, userEmail) {
    const studentEntry = lab.students?.find(s => s.email === userEmail);
    if (studentEntry?.teacher?.email) {
        return studentEntry.teacher.email;
    }
    const isTeacher = lab.email === userEmail || lab.teachers?.some(t => t.email === userEmail);
    if (isTeacher) {
        return userEmail;
    }
    return lab.email;
}


function SubbedLabPage(props) {
    const [labData, setLabData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const userEmail = props.apiAuthResponse;

    const labColumns = useMemo(
        () => [
            {
                accessorKey: 'idLab',
                header: 'ID Laboratorio',
                size: 100,
            },
            {
                accessorKey: 'labName',
                header: 'Nome Laboratorio',
                size: 200,
            },
            {
                accessorKey: 'docenteEmail',
                header: 'Docente',
                size: 200,
            },
            {
                accessorKey: 'corso',
                header: 'Corso',
                size: 200,
            },
            {
                accessorKey: 'linkVdi',
                header: 'Accesso Laboratorio',
                size: 200,
                Cell: ({ row }) => <VdiLinkPopover lab={row.original} />,
            },
        ],
        [],
    );

    const fetchLabs = useCallback(async () => {
        setIsLoading(true);
        try {
            const resp = await fetch(process.env.REACT_APP_URL_GET_PERSONAL_LAB, {
                credentials: 'include',
                method: "GET",
            });
            const labs = await resp.json();

            const processed = labs.map(lab => ({
                ...lab,
                docenteEmail: getDocenteEmail(lab, userEmail),
                corso: lab.adSel?.anaAfDescIta || '-',
            }));

            setLabData(processed);
        } catch (error) {
            console.error("Errore nel caricamento dei laboratori:", error);
            setLabData([]);
        } finally {
            setIsLoading(false);
        }
    }, [userEmail]);

    useEffect(() => {
        fetchLabs();
    }, [fetchLabs]);

    const table = useMaterialReactTable({
        columns: labColumns,
        data: labData,
        initialState: {
            sorting: [{ id: 'idLab', desc: true }],
        },
    });

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#f4f4f4' }}>
            <Box sx={{
                bgcolor: 'white',
                borderBottom: '1px solid #e0e0e0',
                py: 4,
                mb: 4
            }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 0.5, color: '#a71e3b' }}>
                        I Tuoi Laboratori Virtuali
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Visualizza e accedi ai laboratori virtuali a cui sei iscritto
                    </Typography>
                    <Box sx={{
                        mt: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        bgcolor: '#f9f9f9',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            bgcolor: '#a71e3b',
                            borderRadius: '50%',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1.5rem'
                        }}>
                            {labData.length}
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                {labData.length === 1 ? 'Laboratorio attivo' : 'Laboratori attivi'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Hai accesso a {labData.length === 1 ? 'un' : labData.length} ambient{labData.length === 1 ? 'e' : 'i'} di laboratorio
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 3 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <CircularProgress size={60} sx={{ color: '#a71e3b' }} />
                    </Box>
                ) : labData.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Non ci sono laboratori virtuali a cui sei iscritto
                    </Alert>
                ) : (
                    <MaterialReactTable table={table} />
                )}
            </Container>
        </Box>
    );
}

export default SubbedLabPage;
